import React, { useState } from "react";
import { Search, Filter, AlertCircle, ArrowRight } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const AnnouncementsSection = ({ announcements = [] }) => {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = ["all", "General", "Academic", "Sports", "Events"];

  const filteredAnnouncements = announcements.filter(a => {
    const matchesCategory = filter === "all" || a.category === filter;
    const matchesSearch = a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getPriorityBadge = (priority) => {
    const styles = {
      high: "bg-red-100 text-red-600",
      normal: "bg-blue-100 text-blue-600",
      low: "bg-green-100 text-green-600"
    };
    return styles[priority] || styles.normal;
  };

  return (
    <section className="py-16 bg-gradient-to-b from-orange-400 to-yellow-400">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h2 className="text-3xl font-bold text-[#002147]">Announcements</h2>
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search announcements..."
                className="pl-10 w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="announcements-search"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="announcements-filter">
                <Filter size={16} className="mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat === "all" ? "All Categories" : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAnnouncements.map((announcement, index) => (
            <Card key={announcement.id || index} className="card-hover animate-fadeIn bg-white/90 backdrop-blur-sm" style={{ animationDelay: `${index * 0.1}s` }}>
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <Badge variant="outline" className={getPriorityBadge(announcement.priority)}>
                    {announcement.priority}
                  </Badge>
                  <Badge variant="secondary">{announcement.category}</Badge>
                </div>
                <CardTitle className="text-lg">{announcement.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm line-clamp-3">{announcement.content}</p>
              </CardContent>
              <CardFooter className="text-xs text-gray-400">
                <span>By {announcement.author_name}</span>
                <span className="mx-2">•</span>
                <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
              </CardFooter>
            </Card>
          ))}
        </div>

        {filteredAnnouncements.length === 0 && (
          <div className="text-center py-12 bg-white/80 rounded-xl">
            <AlertCircle className="mx-auto mb-4 text-[#002147]" size={48} />
            <h3 className="text-xl font-semibold text-[#002147] mb-2">No Announcements Yet</h3>
            <p className="text-gray-600">Check back soon for important school updates and news.</p>
          </div>
        )}

        <div className="text-center mt-8">
          <Button variant="outline" className="border-[#002147] text-[#002147] bg-white/50 hover:bg-[#002147] hover:text-white">
            View All Announcements <ArrowRight className="ml-2" size={16} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AnnouncementsSection;
